(() => {
  if (customElements.get('power-every-moment')) return;

  class PowerEveryMoment extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.panels = Array.from(this.querySelectorAll('[data-moment-panel]'));
      this.cards = this.panels.map((panel) => panel.querySelector('[data-moment-card]'));
      this.desktopQuery = window.matchMedia('(min-width: 1024px)');
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.desktopActivation = this.dataset.desktopActivation || 'hover';
      this.cleanup = [];
      this.track = this.querySelector('.power-moments__track');

      this.panels.forEach((panel, index) => {
        this.listen(panel, 'pointerenter', () => {
          if (
            this.desktopQuery.matches &&
            this.hoverQuery.matches &&
            this.desktopActivation === 'hover'
          ) {
            this.activate(index);
          }
        });

        this.listen(panel, 'focusin', () => {
          if (this.desktopQuery.matches) this.activate(index);
        });

        this.listen(panel, 'click', (event) => {
          if (!this.desktopQuery.matches || panel.classList.contains('is-active')) return;

          if (this.desktopActivation === 'click' || !this.hoverQuery.matches) {
            event.preventDefault();
            this.activate(index);
          }
        });

        const card = this.cards[index];
        if (card?.classList.contains('power-moments__card--static')) {
          this.listen(card, 'keydown', (event) => {
            if (!this.desktopQuery.matches || !['Enter', ' '].includes(event.key)) return;
            event.preventDefault();
            this.activate(index);
          });
        }
      });

      this.onViewportChange = () => {
        this.syncAccessibility();
        this.syncDesktopLayout();
      };
      this.listen(this.desktopQuery, 'change', this.onViewportChange);

      if (this.track && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.syncDesktopLayout());
        this.resizeObserver.observe(this.track);
        this.cleanup.push(() => this.resizeObserver.disconnect());
      } else {
        this.listen(window, 'resize', () => this.syncDesktopLayout());
      }

      this.onBlockSelect = (event) => {
        if (!event.detail || !event.detail.blockId) return;

        const index = this.panels.findIndex(
          (panel) => panel.dataset.blockId === event.detail.blockId
        );

        if (index < 0) return;

        this.activate(index);
        if (!this.desktopQuery.matches) {
          this.panels[index].scrollIntoView({ block: 'nearest', inline: 'start' });
        }
      };

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.activate(
        Math.max(0, this.panels.findIndex((panel) => panel.classList.contains('is-active')))
      );
    }

    disconnectedCallback() {
      this.cleanup?.forEach((remove) => remove());
      this.cleanup = [];
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.initialized = false;
    }

    listen(target, eventName, handler) {
      target.addEventListener(eventName, handler);
      this.cleanup.push(() => target.removeEventListener(eventName, handler));
    }

    activate(activeIndex) {
      this.syncDesktopLayout(activeIndex);

      this.panels.forEach((panel, index) => {
        const active = index === activeIndex;
        panel.classList.toggle('is-active', active);
        panel.dataset.active = String(active);
      });

      this.syncAccessibility();
    }

    syncDesktopLayout(activeIndex = this.activeIndex) {
      if (!this.track || !this.panels.length) return;

      if (!this.desktopQuery.matches) {
        delete this.dataset.desktopLayoutReady;
        this.style.removeProperty('--pm-expanded-panel-width');
        this.style.removeProperty('--pm-desktop-track-height');
        return;
      }

      const trackStyle = getComputedStyle(this.track);
      const componentStyle = getComputedStyle(this);
      const gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      const activeRatio = Math.max(
        1,
        parseFloat(componentStyle.getPropertyValue('--pm-active-ratio')) || 1
      );
      const availableWidth = Math.max(0, this.track.clientWidth - gap * (this.panels.length - 1));
      const expandedWidth =
        (availableWidth * activeRatio) / (activeRatio + this.panels.length - 1);
      const imageAspect =
        parseFloat(this.panels[activeIndex]?.dataset.desktopAspect) ||
        parseFloat(componentStyle.getPropertyValue('--pm-desktop-media-aspect')) ||
        1;
      const trackHeight = expandedWidth / imageAspect;

      if (expandedWidth > 0 && trackHeight > 0) {
        this.style.setProperty('--pm-expanded-panel-width', `${expandedWidth}px`);
        this.style.setProperty('--pm-desktop-track-height', `${trackHeight}px`);
        this.dataset.desktopLayoutReady = 'true';
      }
    }

    syncAccessibility() {
      this.cards.forEach((card, index) => {
        if (!card) return;

        if (this.desktopQuery.matches) {
          card.setAttribute('aria-expanded', String(index === this.activeIndex));
        } else {
          card.removeAttribute('aria-expanded');
        }
      });
    }

    get activeIndex() {
      return Math.max(0, this.panels.findIndex((panel) => panel.classList.contains('is-active')));
    }
  }

  customElements.define('power-every-moment', PowerEveryMoment);
})();
